/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import type ApplicationInstance from "@ember/application/instance";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { service } from "@ember/service";
import { isBlank } from "@ember/utils";
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { restartableTask, task, timeout } from "ember-concurrency";
import type IntlService from "ember-intl/services/intl";
import type CoreAdapter from "parog-web/adapters/core";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import type { BioExamResultArgs } from "parog-web/models/bio-exam-result";
import BioExamResult from "parog-web/models/bio-exam-result";
import type ExternalFluxLoincMapping from "parog-web/models/external-flux-loinc-mapping";
import type BioExamObservation from "parog-web/models/loinc";
import type FetcherService from "parog-web/services/fetcher";
import parameterize from "parog-web/utils/parameterize";
import { sortBy } from "parog-web/utils/sort";
import trimLoincCode from "parog-web/utils/trim-loinc-code";
import type { BioExamResultsChangeset } from "..";

import styles from "./index.module.css";

interface Args extends ForcBaseArgs {
  bioExamResultChangeset: BioExamResultsChangeset;
  resultIndex: number;
  onChangesetUpdate: (index: number, bioExamResult: BioExamResult) => void;
  onResultRemoved: (index: number) => void;
  source: string;
}

export default class BioExamResultsReviewFormMatchingBoardComponent extends Component<Args> {
  styles = styles;

  applicationAdapter = (getOwner(this) as ApplicationInstance).lookup(
    "adapter:application"
  ) as CoreAdapter;

  @service declare store: Store;
  @service declare fetcher: FetcherService;
  @service declare intl: IntlService;

  /** This is the currently selected code, which could be:
   * - a matched code, in the form `LOINC-XXXX`
   * - a ADHOC code, in the form `ADHOC-XXXX`
   *
   * This format is the format saved in the bio JE.
   */
  @tracked selectedCode: string;
  @tracked shouldBeEmphasized = false;

  /** This is the currently selected raw unit, which will
   * remain undefined if the rawUnit is not null. Else it will
   * be set by the IDE
   */
  get selectedRawUnit() {
    return this.bioExamResultChangeset.get("selectedRawUnit");
  }

  @tracked impossibleConversion;

  declare rawLabel: string;
  declare rawValue: string;
  declare rawUnit: string;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    const rawContent = this.args.bioExamResultChangeset.get(
      "resultHash.originalLine"
    ).content.raw;

    // These are raw content we want to display in the "Résutat reçu" column.
    this.rawLabel =
      this.args.source == "hprim" ? rawContent.segs[1] : rawContent.label;

    this.rawValue =
      this.args.source == "hprim" ? rawContent.segs[4] : rawContent.value;

    this.rawUnit =
      this.args.source == "hprim" ? rawContent.segs[5] : rawContent.unit;

    // This property is used to store the current value of the selected loinc code
    this.selectedCode = this.args.bioExamResultChangeset.get("code");

    if (this.selectedLoinc && !this.isIgnored) {
      this.getEquivalentObservationUnit(this.selectedLoinc);
    }
  }

  @action
  onAccordionOpen() {
    this.shouldBeEmphasized = true;
  }

  @action
  onAccordionClose() {
    this.shouldBeEmphasized = false;
  }

  get bioExamResultChangeset(): BioExamResultsChangeset {
    return this.args.bioExamResultChangeset;
  }

  get isAdhoc(): boolean {
    return !this.selectedCode?.includes("LOINC");
  }

  get isIgnored(): boolean {
    return this.selectedLoinc == "ignore";
  }

  /**
   * Is true when the result is not ignored
   * Otherwise check the ADHOC value
   */
  get shouldShowMatchedValues() {
    if (this.isIgnored) return false;

    return !this.isAdhoc;
  }

  /**
   * Returns the trimed version of the selected LOINC code.
   * It is usefull to search for `BioExamObserations`
   *
   * It's undefined when the result is ADHOC.
   */
  get selectedLoinc(): string | undefined {
    if (this.isAdhoc) return;
    return trimLoincCode(this.selectedCode);
  }

  /**
   * Returns the current `bioExamObservation` corresponding to the `selectedLoinc`
   * Returns undefined when there is no `selectedLoinc`
   */
  get currentBioExamObservation(): BioExamObservation | undefined {
    if (!this.selectedLoinc) return;

    return this.store.peekRecord(
      "loinc",
      trimLoincCode(this.selectedLoinc)
    ) as BioExamObservation;
  }

  /**
   * Returns a list of `BioExamObservation` which could be matched with
   * the HPRIM result, based on the raw label.
   */
  get suggestedObservations(): BioExamObservation[] | undefined {
    const mappings = this.store.peekAll("external-flux-loinc-mapping");

    const parameterizedLabel = parameterize(this.rawLabel);

    const words = parameterizedLabel.split("-");
    const suggestedObservation = mappings.reduce(
      (acc: BioExamObservation[], curr: ExternalFluxLoincMapping) => {
        if (!curr.bioExamObservation) return acc;
        words.forEach(async word => {
          // We first try to find a `bioExamObservation` which includes raw label
          if (word.length > 2 && curr.label.includes(word)) {
            acc.push(curr.bioExamObservation);
          }
        });

        return acc;
      },
      []
    );

    /* In some case, a result could have automatically be mapped by the back, but
    in the front side, we can retrieve the corresponding `BioExamObservation` in
    the selection dropdown. So we force the addition in the matchable array. */
    if (this.currentBioExamObservation) {
      suggestedObservation.push(this.currentBioExamObservation);
    }

    // Remove duplicates in `suggestedObservation` array
    return [...new Set(suggestedObservation)].sort(sortBy("displayLabel"));
  }

  get displayedOperator(): string {
    const operator = this.bioExamResultChangeset.get("operator");

    let displayedOperator;
    switch (operator) {
      case "greater-than":
        displayedOperator = "≥";
        break;
      case "lower-than":
        displayedOperator = "≤";
        break;
      default:
        displayedOperator = "";
        break;
    }

    return displayedOperator;
  }

  @action
  updateRawValue(event) {
    const value = event.target.value.replace(",", ".");
    const segs = this.bioExamResultChangeset.get(
      "resultHash.originalLine.raw.segs"
    );
    if (segs) {
      segs[4] = value;
      this.bioExamResultChangeset.set("resultHash.originalLine.raw.segs", segs);
    }
    this.bioExamResultChangeset.set("value", value);

    this.args.onChangesetUpdate(
      this.args.resultIndex,
      this.toBioExamResult(this.currentBioExamObservation)
    );

    this.rawValue = value;
  }

  /**
   * Update the state of the changeset with data corresponding to the
   * selected LOINC code. Then calls the parent function to update the
   * global changeset.
   */
  @action
  async updateResult(code: string): Promise<void> {
    this.impossibleConversion = null;
    /* If the code we select on the dropdown is the same than the currently
    selected one, we do nothing. */
    if (this.selectedCode == code) return;

    if (code === "adhoc" || code === "ignore") {
      this.selectedCode =
        code === "adhoc" ? this.generateAdhocCode() : "LOINC-ignore";
      this.reinitializeResultObject();

      this.args.onChangesetUpdate(
        this.args.resultIndex,
        this.toBioExamResult()
      );

      return;
    }

    if (!(code == "ignore" || code == "adhoc")) {
      /* The result may have been previously normalized, we want to make sure
       * the `before_normalization` field is not present anymore in the
       * `originalLine` object to avoid getting inconsistent value.
       *
       * If the result has to be converted, then the `before_normalization`
       * field will be set in the `convertResult` function
       */
      const originalLine = this.bioExamResultChangeset.get(
        "resultHash.originalLine"
      ).content;
      this.bioExamResultChangeset.set("resultHash.originalLine", {
        raw: originalLine.raw,
        uuid: originalLine.uuid
      });
    }

    this.selectedCode = "LOINC-" + code;
    if (!this.currentBioExamObservation) return;

    this.bioExamResultChangeset.set("code", this.selectedCode);
    this.bioExamResultChangeset.set(
      "label",
      this.currentBioExamObservation.displayLabel
    );
    this.bioExamResultChangeset.set("value", this.rawValue);

    /* If the raw unit is different than the unit corresponding to the
    selected LOINC, then we try to convert the result. */
    if (
      this.rawUnit &&
      this.rawUnit.toLowerCase() !=
        this.currentBioExamObservation.unit?.toLowerCase()
    ) {
      await this.convertResult(this.rawUnit);
    }

    /* If the raw unit is undefined, we get the equivalent LOINC
    if it exist to suggest its related unit.
    For instance, if the selected LOINC is in `masse/volume` we try
    to get the equivalent LOINC in `moles/volume` to suggest the
    conversion
    */
    if (!this.rawUnit) {
      await this.getEquivalentObservationUnit(
        this.currentBioExamObservation.loinc
      );
    }

    if (this.shouldUseObsUnit) {
      this.bioExamResultChangeset.set(
        "unit",
        this.currentBioExamObservation.unit
      );
    }

    this.args.onChangesetUpdate(
      this.args.resultIndex,
      this.toBioExamResult(this.currentBioExamObservation)
    );
  }

  /**
   * Returns an instance of the `BioExamResult` class, which aims to
   * be saved in a bio JE.
   */
  toBioExamResult(bioExamObservation?: BioExamObservation): BioExamResult {
    const originalLineChangeset = this.bioExamResultChangeset.get(
      "resultHash.originalLine"
    );
    const originalLine = Object.keys(originalLineChangeset.changes).length
      ? originalLineChangeset.changes
      : originalLineChangeset.content;

    return new BioExamResult({
      code: this.selectedCode,
      unit: bioExamObservation
        ? bioExamObservation.unit
        : this.bioExamResultChangeset.get("unit"),
      label: bioExamObservation
        ? bioExamObservation.displayLabel
        : this.bioExamResultChangeset.get("label"),
      value: this.bioExamResultChangeset.get("value"),
      store: this.store,
      originalLine,
      normIndicator: this.bioExamResultChangeset.get(
        "resultHash.normIndicator"
      ),
      normValInf: this.bioExamResultChangeset.get("resultHash.normValInf"),
      normValSupp: this.bioExamResultChangeset.get("resultHash.normValSupp"),
      status: this.bioExamResultChangeset.get("resultHash.status"),
      operator: this.bioExamResultChangeset.get("operator"),
      selectedRawUnit: this.bioExamResultChangeset.get("selectedRawUnit")
    });
  }

  /**
   * Converts the result and updates the unit & value of the result
   */
  @action
  async updateUnit(selectedRawUnit: string): Promise<void> {
    this.impossibleConversion = null;

    if (selectedRawUnit == "adhoc") {
      this.bioExamResultChangeset.set("selectedRawUnit", undefined);
      this.reinitializeValue();

      this.args.onChangesetUpdate(
        this.args.resultIndex,
        this.toBioExamResult()
      );
      return;
    }

    await this.convertResult(selectedRawUnit);

    this.bioExamResultChangeset.set("selectedRawUnit", selectedRawUnit);

    this.args.onChangesetUpdate(
      this.args.resultIndex,
      this.toBioExamResult(this.currentBioExamObservation)
    );
  }

  @action
  updateOperator(selected: string) {
    this.bioExamResultChangeset.set("operator", selected);

    this.args.onChangesetUpdate(
      this.args.resultIndex,
      this.toBioExamResult(this.currentBioExamObservation)
    );
  }

  @service requestManager;

  async getEquivalentObservationUnit(loinc: string): Promise<void> {
    const { response, content } = await this.requestManager.request({
      url: `/api/v1/equivalent_loinc/${loinc}`
    });

    // No content response means there are no equivalent LOINC
    if (response.status == 204) return;
    if (!content) return;
    // The user may have deselected a bio before the API could answer.
    if (!this.currentBioExamObservation) return;

    this.currentBioExamObservation.equivalentLoincUnit =
      content.data.attributes.wiseUnit;
  }

  /**
   * Calls an endpoint to convert a bio exam result. The payload is
   * simply the state of the current changeset, as an instance of
   * the `BioExamResult` class.
   * The response of the request contains converted values, that we
   * put back in the changeset.
   */
  async convertResult(sourceUnit: string): Promise<void> {
    const resultToSend = new BioExamResult({
      code: this.bioExamResultChangeset.get("code"),
      unit: sourceUnit,
      value: this.rawValue,
      originalLine: this.bioExamResultChangeset.get("resultHash.originalLine")
        .content,
      normValInf: this.bioExamResultChangeset.get("resultHash.normValInf"),
      normValSupp: this.bioExamResultChangeset.get("resultHash.normValSupp")
    });

    const body = JSON.stringify({
      data: {
        attributes: {
          bioExam: resultToSend.serialize(),
          patientUuid: this.args.patient?.uuid
        }
      }
    });

    try {
      const { content } = await this.requestManager.request({
        url: "/api/v1/convert_bio_exam",
        method: "POST",
        body: body
      });
      const bioExamResult: BioExamResultArgs = content.data.attributes.result;
      if (bioExamResult.code.startsWith("LOINC")) {
        this.bioExamResultChangeset.set("value", bioExamResult.value);
        this.bioExamResultChangeset.set(
          "resultHash.normValInf",
          bioExamResult.normValInf
        );
        this.bioExamResultChangeset.set(
          "resultHash.normValSupp",
          bioExamResult.normValSupp
        );

        const originalLine = this.bioExamResultChangeset.get(
          "resultHash.originalLine"
        ).content;
        this.bioExamResultChangeset.set("resultHash.originalLine", {
          raw: originalLine.raw,
          uuid: originalLine.uuid,
          before_normalization: bioExamResult.originalLine?.before_normalization
        });

        this.bioExamResultChangeset.set(
          "unit",
          (this.currentBioExamObservation as BioExamObservation).unit
        );
      } else {
        this.reinitializeValue();
        this.impossibleConversion = this.setWrongConversionMessage(
          sourceUnit,
          (this.currentBioExamObservation as BioExamObservation).unit as string
        );
      }
    } catch (error) {
      new Error(error);
    }
  }

  /**
   * Temporary message to display when the Matchlab couldn't convert a bio exam
   * Will later be removed
   */
  setWrongConversionMessage(sourceUnit: string, targetUnit: string): string {
    return `Impossible de convertir des ${sourceUnit} en ${targetUnit}.`;
  }

  generateAdhocCode(): string {
    const sourceCode =
      this.args.source == "hprim"
        ? this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw.segs?.[2]
        : this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw.code;

    return BioExamResult.generateAdhocCode(`${this.rawLabel}(${sourceCode})`);
  }

  reinitializeResultObject(): void {
    this.bioExamResultChangeset.set("code", this.selectedCode);
    this.bioExamResultChangeset.set("unit", this.rawUnit);
    this.bioExamResultChangeset.set("label", this.rawLabel);

    this.reinitializeValue();
  }

  reinitializeValue(): void {
    this.bioExamResultChangeset.set("value", this.rawValue);

    const normValInf =
      this.args.source == "hprim"
        ? this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw.segs?.[6]
        : this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw["norm-val-inf"];
    this.bioExamResultChangeset.set("resultHash.normValInf", normValInf);

    const normValSupp =
      this.args.source == "hprim"
        ? this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw.segs?.[7]
        : this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw["norm-val-supp"];
    this.bioExamResultChangeset.set("resultHash.normValSupp", normValSupp);

    const normIndicator =
      this.args.source == "hprim"
        ? this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw.segs?.[8]
        : null;
    this.bioExamResultChangeset.set("resultHash.normIndicator", normIndicator);

    const status =
      this.args.source == "hprim"
        ? this.args.bioExamResultChangeset.get("resultHash.originalLine")
            .content.raw.segs?.[9]
        : null;
    this.bioExamResultChangeset.set("resultHash.status", status);

    const originalLine = this.bioExamResultChangeset.get(
      "resultHash.originalLine"
    ).content;

    this.bioExamResultChangeset.set("resultHash.originalLine", {
      raw: originalLine.raw,
      uuid: originalLine.uuid
    });
  }

  get shouldUseObsUnit(): boolean {
    if (this.isIgnored || this.isAdhoc) return false;

    if (
      !this.rawUnit &&
      (this.selectedRawUnit ||
        !(this.currentBioExamObservation as BioExamObservation).availableUnits
          .length)
    ) {
      return true;
    }

    if (this.rawUnit) return true;

    return false;
  }

  queryOtherObservations = restartableTask(async (value: string) => {
    if (isBlank(value)) {
      return [];
    }

    await timeout(500);

    const bioExamObservations = await this.getRecords.perform(value);
    return bioExamObservations;
  });

  getRecords = task(async (value: string) => {
    return await this.fetcher.loadRecords("loinc", {
      filter: {
        label: value
      }
    });
  });

  @action
  deleteManualResult() {
    this.args.onResultRemoved(this.args.resultIndex);
  }
}
